/**
 * Journey Hooks
 *
 * React Query hooks for Journey operations.
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import { useAuth } from '../providers/AuthProvider';
import { homeKeys } from './home-keys';
import {
  createJourneyService,
  type ArchivedJourneyList,
  type CitizenArchiveResult,
  type CitizenRestoreResult,
  type CitizenSelectResult,
  type ConsentScopeName,
  type Journey,
  type JourneyConsentDetail,
  type JourneyConsentUpdateResult,
  type CreateJourneyInput,
  type LeaveProgrammeResult,
} from '../services/journey.service';

// Query keys
export const journeyKeys = {
  all: ['journeys'] as const,
  lists: () => [...journeyKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...journeyKeys.lists(), filters] as const,
  details: () => [...journeyKeys.all, 'detail'] as const,
  detail: (id: string) => [...journeyKeys.details(), id] as const,
  current: () => [...journeyKeys.all, 'current'] as const,
  /** W8.1 · F47 — the citizen's own archived journeys. */
  archived: () => [...journeyKeys.all, 'archived'] as const,
  archivedCount: () => [...journeyKeys.all, 'archived', 'count'] as const,
  /** W8.D — who holds what of this journey, and the trail behind it. */
  consents: (id: string) => [...journeyKeys.all, 'consents', id] as const,
  /** The server's progress through each hero of this journey. */
  progress: (id: string) => [...journeyKeys.all, 'progress', id] as const,
  /** R4 — what the citizen already holds on one origin → destination. */
  routeAdvice: (origin: string, destination: string) =>
    [...journeyKeys.all, 'route-advice', origin, destination] as const,
};

/**
 * Get all journeys
 */
export function useJourneys() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const journeyService = createJourneyService(client);

  return useQuery({
    queryKey: journeyKeys.lists(),
    queryFn: () => journeyService.getJourneys(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Get journey by ID
 */
export function useJourney(id: string) {
  const { client } = useSdkContext();
  const journeyService = createJourneyService(client);

  return useQuery({
    queryKey: journeyKeys.detail(id),
    queryFn: () => journeyService.getJourney(id),
    enabled: !!id,
  });
}

/**
 * Get current active journey
 */
export function useCurrentJourney() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const journeyService = createJourneyService(client);

  return useQuery({
    queryKey: journeyKeys.current(),
    queryFn: () => journeyService.getCurrentJourney(),
    enabled: isSignedIn && !!token,
  });
}

/** `journey` in `list` by id: replaced where it is, appended where it is not. */
export function upsertJourney(list: Journey[] | undefined, journey: Journey): Journey[] {
  if (!list) return [journey];
  return list.some((j) => j.id === journey.id)
    ? list.map((j) => (j.id === journey.id ? journey : j))
    : [...list, journey];
}

/**
 * Create a new journey instance with full configuration.
 */
export function useCreateJourney() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const journeyService = createJourneyService(client);

  return useMutation({
    mutationFn: (input: CreateJourneyInput) => journeyService.createJourney(input),
    onSuccess: (newJourney) => {
      // Into the list cache, ONCE. A replayed create (R3) — or the legacy
      // request reusing the route's journey — returns a journey the list may
      // already hold, and appending it put the same journey in the list twice
      // until the refetch landed: two cards, one React key.
      queryClient.setQueryData<Journey[]>(journeyKeys.lists(), (old) =>
        upsertJourney(old, newJourney)
      );
      // Set as current journey
      queryClient.setQueryData(journeyKeys.current(), newJourney);
      // Invalidate queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
      // Home moves on with the journey: Welcome becomes Getting ready, on the
      // phone and the web alike — nothing to reload.
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
      // The route now holds one more journey.
      queryClient.invalidateQueries({ queryKey: [...journeyKeys.all, 'route-advice'] });
    },
  });
}

/**
 * Ask the server what the citizen already holds on a route (R4) — the
 * builder's last step before "Build". Imperative, because the builder asks at
 * one moment (the route is chosen) and acts on the answer; the answer is
 * cached under the route, and every create invalidates it.
 */
export function useFetchRouteAdvice() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const journeyService = createJourneyService(client);

  return useCallback(
    (origin: string, destination: string) =>
      queryClient.fetchQuery({
        queryKey: journeyKeys.routeAdvice(origin, destination),
        queryFn: () => journeyService.getRouteAdvice(origin, destination),
        staleTime: 0,
      }),
    [queryClient, journeyService]
  );
}

/** Name one of the citizen's journeys, or clear its name with `null` (D-F). */
export function useSetJourneyLabel() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const journeyService = createJourneyService(client);

  return useMutation<
    { journeyId: string; label: string | null },
    Error,
    { journeyId: string; label: string | null }
  >({
    mutationFn: ({ journeyId, label }) => journeyService.setJourneyLabel(journeyId, label),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.archived() });
    },
  });
}

/**
 * The journey's progress through each hero — the overall ring, the per-hero
 * bars and the stepper on the citizen dashboards draw it, and compute nothing.
 */
export function useJourneyProgress(journeyId: string) {
  const { client } = useSdkContext();
  const journeyService = createJourneyService(client);

  return useQuery({
    queryKey: journeyKeys.progress(journeyId),
    queryFn: () => journeyService.getJourneyProgress(journeyId),
    enabled: !!journeyId,
  });
}

// NOTE: `useUpdateProgress` was deleted with F48, together with the
// `JourneyService.updateProgress` method it wrapped: both addressed
// `PATCH /api/journeys/:id/progress`, a route platform-api has never served.
// No app imported the hook.

// ═══════════════════════════════════════════════════════════════════════
// CITIZEN JOURNEY MANAGEMENT (W8.1 · F47)
// ═══════════════════════════════════════════════════════════════════════
//
// NAMING: `…MyJourney`, not `…Journey`. `useArchiveJourney` and
// `useUnarchiveJourney` already exist in `useBusiness.ts` and are the COMPANY's
// verbs — different endpoint, different subject, different rules. The barrel
// re-exports both files, so a shared name is a build error today and would have
// been a silently-wrong import the moment the barrel stopped being flat.

/**
 * The journeys the citizen has archived themselves.
 *
 * Note what this does NOT return: household data. `GET /journeys/:id` carries
 * `familyMembers`, and that read resolves a journey only to find its head
 * talent before returning the talent's WHOLE roster across every journey they
 * hold (H6) — names, dates of birth and passport numbers included. An archived
 * card needs none of it, so the endpoint behind this hook never asks.
 */
export function useMyArchivedJourneys() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const journeyService = createJourneyService(client);

  return useQuery<ArchivedJourneyList, Error>({
    queryKey: journeyKeys.archived(),
    queryFn: () => journeyService.getArchivedJourneys(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * How many journeys the citizen has archived.
 *
 * Its own query because the zero-journey empty state needs the number when
 * there is no journey to read it from — the case D-29 turns on. Archiving the
 * LAST journey is allowed because the builder always renders; the gap was the
 * route back, and this count is what lets the empty state offer "recover an
 * archived journey" beside "build one" instead of offering it blindly.
 */
export function useMyArchivedJourneyCount() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const journeyService = createJourneyService(client);

  return useQuery<{ archivedCount: number }, Error>({
    queryKey: journeyKeys.archivedCount(),
    queryFn: () => journeyService.getArchivedJourneyCount(),
    enabled: isSignedIn && !!token,
  });
}

/**
 * Archive a journey.
 *
 * NO optimistic update, deliberately. The archive can be refused server-side
 * (a live company programme, a running sponsorship) and it can promote a
 * different journey to primary — neither of which the client can predict. W2's
 * F18 post-mortem is the precedent: business-web's optimistic filter removed
 * every card of a person while decrementing the total by one, and the two
 * disagreed until a 60-second poll corrected them. The Undo affordance is a
 * client-side toast over the real result, not a guess at it.
 */
export function useArchiveMyJourney() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const journeyService = createJourneyService(client);

  return useMutation<CitizenArchiveResult, Error, string>({
    mutationFn: (journeyId: string) => journeyService.archiveJourney(journeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.archived() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.archivedCount() });
    },
  });
}

/** Restore a journey the citizen archived. This is also what Undo calls. */
export function useRestoreMyJourney() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const journeyService = createJourneyService(client);

  return useMutation<CitizenRestoreResult, Error, string>({
    mutationFn: (journeyId: string) => journeyService.restoreJourney(journeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.archived() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.archivedCount() });
    },
  });
}

/**
 * Choose a journey — persist it as the citizen's primary. (F47 · D-5)
 *
 * The switcher's tap used to write only this device's storage, so the server's
 * idea of the current journey (`is_primary` — hero access, `/journeys/current`,
 * the citizen's other device) never moved. The providers call this on every
 * user-initiated switch.
 */
export function useSelectMyJourney() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const journeyService = createJourneyService(client);

  return useMutation<CitizenSelectResult, Error, string>({
    mutationFn: (journeyId: string) => journeyService.selectJourney(journeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
    },
  });
}

/**
 * Leave a company's programme — which ENDS that company's relationship (D1).
 *
 * Everything that reads the relationship's state is invalidated: the journey
 * list and the current journey (the card's badge says "You left" and its Leave
 * goes), the journey's consents ("What they see" turns read-only), and Home
 * (the company's requests leave Needs you).
 */
export function useLeaveCompanyProgramme() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const journeyService = createJourneyService(client);

  return useMutation<LeaveProgrammeResult, Error, { journeyId: string; companyTenantId: string }>({
    mutationFn: ({ journeyId, companyTenantId }) =>
      journeyService.leaveCompanyProgramme(journeyId, companyTenantId),
    onSuccess: (_result, { journeyId }) => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.current() });
      queryClient.invalidateQueries({ queryKey: journeyKeys.consents(journeyId) });
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

// ── Per-scope consent (W8.D) ─────────────────────────────────────────────

/** Who holds what of this journey, with the trail behind each grant. */
export function useJourneyConsents(journeyId: string) {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useAuth();
  const journeyService = createJourneyService(client);

  return useQuery<JourneyConsentDetail, Error>({
    queryKey: journeyKeys.consents(journeyId),
    queryFn: () => journeyService.getConsentDetail(journeyId),
    enabled: isSignedIn && !!token && !!journeyId,
  });
}

/**
 * Set exactly which scopes ONE company may read on this journey.
 *
 * Invalidates the consent read, and Home: whether a company can see the
 * CheckUp decides Home's "Choose what they see" card. The journey list does
 * not move — a relationship's state is not its consent.
 */
export function useSetJourneyConsentScopes(journeyId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const journeyService = createJourneyService(client);

  return useMutation<
    JourneyConsentUpdateResult,
    Error,
    { companyTenantId: string; scopes: ConsentScopeName[] }
  >({
    mutationFn: ({ companyTenantId, scopes }) =>
      journeyService.setConsentScopes(journeyId, companyTenantId, scopes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journeyKeys.consents(journeyId) });
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}
